import { c as _c } from "react/compiler-runtime";
import { isMaskedDob } from '@onefootprint/core';
import { getHostedUserTokenOptions, patchHostedUserVault, postHostedOnboardingPrefillVerifiedCommit, postHostedUserVaultDecryptOptions } from '@onefootprint/fetch-hosted';
import type { VaultData } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationOverride } from '@/idv2/features/app-shell';
import { NavigationHeader } from '@/idv2/features/app-shell/navigation-header';
import { CollectData, type CollectDataMode } from '@/idv2/features/collect-data';
import { canShowCompactReview, getCollectDataVaultData } from '@/idv2/features/collect-data/utils/collect-data-helpers';
import { fetchPrefillData } from '@/idv2/features/collect-data/utils/fetch-prefill-data';
import { filterSensitiveDis } from '@/idv2/features/collect-data/utils/filter-sensitive-dis';
import { getCollectDataDis } from '@/idv2/features/collect-data/utils/get-collect-data-dis';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { type FieldError, useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { filterBootstrapData } from '@/idv2/utils/filter-bootstrap-data';
import { isOnboardingSuperseded } from '@/idv2/utils/is-onboarding-superseded';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'collect-data'
});
export const Route = createFileRoute('/onboarding/$token/collect-data')({
  beforeLoad: async ({
    context: {
      currentRequirement,
      queryClient
    },
    params: {
      token
    }
  }) => {
    if (currentRequirement?.kind !== 'collect_data') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const headers = {
      'X-Fp-Authorization': token
    };

    // Fetch token scopes to determine which fields we can decrypt
    const tokenInfo = await queryClient.fetchQuery(getHostedUserTokenOptions({
      headers
    }));

    // Get all fields for the requirement, then filter based on token permissions
    const allFields = getCollectDataDis(currentRequirement);
    const decryptableFields = filterSensitiveDis(allFields, tokenInfo.scopes);
    logInfo('Collect data decrypting vault fields', {
      allFields: allFields.join(','),
      decryptableFields: decryptableFields.join(','),
      scopes: tokenInfo.scopes.join(',')
    });
    const fullVault = await queryClient.fetchQuery(postHostedUserVaultDecryptOptions({
      headers,
      body: {
        fields: decryptableFields
      }
    }));
    return {
      currentRequirement,
      fullVault
    };
  },
  loader: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement,
      config,
      fullVault,
      kycBootstrapData
    }
  }) => {
    // Fetch prefill data (Footprint prefill takes precedence over Verified prefill)
    const {
      allVaultData,
      verifiedPrefillResponse,
      footprintPrefillContext,
      footprintPrefillChallengeData
    } = await fetchPrefillData({
      queryClient,
      token,
      currentRequirement,
      fullVault,
      config
    });

    // Filter to only the fields needed for collect-data
    const decryptedVault = getCollectDataVaultData(currentRequirement, allVaultData);

    // Get allowed fields and filter bootstrap data to only include fields collected by the playbook
    const allowedFields = getCollectDataDis(currentRequirement);
    const {
      filteredData: filteredBootstrapData,
      bootstrapFields
    } = filterBootstrapData(kycBootstrapData, allowedFields);

    // Merge filtered bootstrap data with decrypted vault (bootstrap takes precedence)
    const mergedVault: VaultData = {
      ...decryptedVault,
      ...filteredBootstrapData
    };
    const manualEntryVault: VaultData = {
      ...getCollectDataVaultData(currentRequirement, fullVault),
      ...filteredBootstrapData
    };
    logInfo('Collect data filtered vault', {
      screenFields: Object.keys(decryptedVault).join(','),
      bootstrapFields: bootstrapFields.join(',')
    });
    return {
      decryptedVault: mergedVault,
      manualEntryVault,
      currentRequirement,
      verifiedPrefillResponse,
      footprintPrefillContext,
      footprintPrefillChallengeData,
      bootstrapFields
    };
  },
  component: CollectDataPage
});
function CollectDataPage() {
  const $ = _c(51);
  const loaderData = Route.useLoaderData();
  const {
    currentRequirement,
    footprintPrefillContext,
    footprintPrefillChallengeData,
    bootstrapFields
  } = loaderData;
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const {
    l10n,
    config
  } = Route.useRouteContext();
  const {
    t
  } = useTranslation("idv2");
  const {
    toast
  } = useToast();
  const handleVaultError = useVaultMutationError();
  const disableSsnValidation = !config.isLive;
  const [isVerifiedPrefillDropped, setIsVerifiedPrefillDropped] = useState(false);
  const decryptedVault = isVerifiedPrefillDropped ? loaderData.manualEntryVault : loaderData.decryptedVault;
  const verifiedPrefillResponse = isVerifiedPrefillDropped ? undefined : loaderData.verifiedPrefillResponse;
  const hasOverlay = Boolean(footprintPrefillContext) || verifiedPrefillResponse?.result?.kind === "step_up";
  let t0;
  if ($[0] !== currentRequirement || $[1] !== decryptedVault || $[2] !== hasOverlay) {
    t0 = canShowCompactReview(currentRequirement, decryptedVault, hasOverlay);
    $[0] = currentRequirement;
    $[1] = decryptedVault;
    $[2] = hasOverlay;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const canShowReview = t0;
  const [mode, setMode] = useState(canShowReview ? "review" : "edit");
  const {
    setNavigationOverridden
  } = useNavigationOverride();
  const isEditingFromReview = canShowReview && mode === "edit";
  let t1;
  let t2;
  if ($[4] !== isEditingFromReview || $[5] !== setNavigationOverridden) {
    t1 = () => {
      if (!isEditingFromReview) {
        return;
      }
      setNavigationOverridden(true);
      return () => setNavigationOverridden(false);
    };
    t2 = [isEditingFromReview, setNavigationOverridden];
    $[4] = isEditingFromReview;
    $[5] = setNavigationOverridden;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let headers;
  if ($[8] !== bootstrapFields || $[9] !== token) {
    headers = {
      "X-Fp-Authorization": token
    };
    if (bootstrapFields.length > 0) {
      let t3;
      if ($[11] !== bootstrapFields) {
        t3 = bootstrapFields.join(",");
        $[11] = bootstrapFields;
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      headers["X-Fp-Bootstrapped-Fields"] = t3;
    }
    $[8] = bootstrapFields;
    $[9] = token;
    $[10] = headers;
  } else {
    headers = $[10];
  }
  let t3;
  if ($[13] !== headers) {
    t3 = {
      mutationFn: prefillId => postHostedOnboardingPrefillVerifiedCommit({
        headers,
        body: {
          prefillId
        }
      })
    };
    $[13] = headers;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const {
    mutateAsync: commitVerifiedPrefill,
    isPending: isCommitPending
  } = useMutation(t3);
  let t4;
  if ($[15] !== headers) {
    t4 = {
      mutationFn: data => patchHostedUserVault({
        headers,
        body: data
      })
    };
    $[15] = headers;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const {
    mutateAsync: vaultMutation,
    isPending: isVaultMutationPending
  } = useMutation(t4);
  let t5;
  if ($[17] !== commitVerifiedPrefill || $[18] !== footprint.navigation || $[19] !== handleVaultError || $[20] !== t || $[21] !== toast || $[22] !== vaultMutation || $[23] !== verifiedPrefillResponse) {
    t5 = async (data_0, onError) => {
      logInfo("Form submission started", {
        vaultKeys: Object.keys(data_0).join(",")
      });
      if (verifiedPrefillResponse?.result?.kind === "located") {
        ;
        try {
          await commitVerifiedPrefill(verifiedPrefillResponse.result.id);
        } catch (t6) {
          const error = t6;
          if (isOnboardingSuperseded(error)) {
            return;
          }
          logWarn("Verified prefill commit failed, falling back to manual entry", error);
          setIsVerifiedPrefillDropped(true);
          setMode("edit");
          toast({
            title: t(_temp),
            description: t(_temp2),
            variant: "error"
          });
          return;
        }
      }
      ;
      try {
        await vaultMutation(data_0);
        logInfo("Vault mutation succeeded");
        footprint.navigation.forward();
      } catch (t7) {
        const error_0 = t7;
        const fieldErrors = handleVaultError(error_0, onError);
        const log = fieldErrors.length > 0 ? logWarn : logError;
        log("Vault mutation failed", error_0, {
          fieldErrorCount: fieldErrors.length
        });
      }
    };
    $[17] = commitVerifiedPrefill;
    $[18] = footprint.navigation;
    $[19] = handleVaultError;
    $[20] = t;
    $[21] = toast;
    $[22] = vaultMutation;
    $[23] = verifiedPrefillResponse;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const onSubmit = t5;
  const ssn9DigitCount = (decryptedVault["id.ssn9"]?.match(/\d/g) || []).length;
  const scrubbedSsn9Value = ssn9DigitCount === 4 ? decryptedVault["id.ssn9"] : undefined;
  let t6;
  if ($[25] !== decryptedVault) {
    t6 = isMaskedDob(decryptedVault["id.dob"]) ? decryptedVault["id.dob"] : undefined;
    $[25] = decryptedVault;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const scrubbedDobValue = t6;
  let t7;
  if ($[27] !== footprint.navigation) {
    t7 = async () => {
      logInfo("Prefill completed, navigating forward");
      await footprint.navigation.forward({
        confirmations: "wait"
      });
    };
    $[27] = footprint.navigation;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const onPrefill = t7;
  let t8;
  if ($[29] !== isEditingFromReview) {
    t8 = isEditingFromReview && <NavigationHeader canGoBack={true} onBack={() => setMode("review")} />;
    $[29] = isEditingFromReview;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const t9 = isVerifiedPrefillDropped ? "manual-entry" : "initial";
  const t10 = isCommitPending || isVaultMutationPending;
  let t11;
  if ($[31] !== canShowReview || $[32] !== currentRequirement || $[33] !== decryptedVault || $[34] !== disableSsnValidation || $[35] !== footprintPrefillChallengeData || $[36] !== footprintPrefillContext || $[37] !== l10n || $[38] !== mode || $[39] !== onPrefill || $[40] !== onSubmit || $[41] !== scrubbedDobValue || $[42] !== scrubbedSsn9Value || $[43] !== t10 || $[44] !== t9 || $[45] !== token || $[46] !== verifiedPrefillResponse) {
    t11 = <CollectData key={t9} l10n={l10n} isLoading={t10} onSubmit={onSubmit} authToken={token} decryptedVault={decryptedVault} currentRequirement={currentRequirement} disableSsnValidation={disableSsnValidation} verifiedPrefillResponse={verifiedPrefillResponse} scrubbedSsn9Value={scrubbedSsn9Value} scrubbedDobValue={scrubbedDobValue} onPrefill={onPrefill} footprintPrefillContext={footprintPrefillContext} footprintPrefillChallengeData={footprintPrefillChallengeData} canShowReview={canShowReview} mode={mode} onModeChange={setMode} />;
    $[31] = canShowReview;
    $[32] = currentRequirement;
    $[33] = decryptedVault;
    $[34] = disableSsnValidation;
    $[35] = footprintPrefillChallengeData;
    $[36] = footprintPrefillContext;
    $[37] = l10n;
    $[38] = mode;
    $[39] = onPrefill;
    $[40] = onSubmit;
    $[41] = scrubbedDobValue;
    $[42] = scrubbedSsn9Value;
    $[43] = t10;
    $[44] = t9;
    $[45] = token;
    $[46] = verifiedPrefillResponse;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t11 || $[49] !== t8) {
    t12 = <>{t8}{t11}</>;
    $[48] = t11;
    $[49] = t8;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
}
function _temp2($_0) {
  return $_0.collectData.pages.verifiedPrefill.manualEntryToast.description;
}
function _temp($) {
  return $.collectData.pages.verifiedPrefill.manualEntryToast.title;
}